import {
  authConfigSchema,
  type Config,
  type ConfigSection,
  configSchema,
  isHrLead,
  type PaletteColor,
  type PublicUser,
  SECTION_LABELS,
  USERS_CONFLICT_MESSAGE,
  usersListResponseSchema,
  validateConfig,
} from '@hrms/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { IconType } from 'react-icons'
import {
  LuInfo,
  LuPlus,
  LuSignal,
  LuTag,
  LuTriangleAlert,
  LuUsers,
  LuWorkflow,
} from 'react-icons/lu'
import { useBlocker, useSearchParams } from 'react-router'
import { ME_KEY, useCurrentUser } from '../../../auth/AuthProvider.tsx'
import { Button } from '../../../components/ui/Button.tsx'
import { EmptyState } from '../../../components/ui/EmptyState.tsx'
import { useToast } from '../../../components/ui/Toast.tsx'
import { ApiError, apiGet, apiSend } from '../../../lib/api.ts'
import { cn } from '../../../lib/cn.ts'
import { CONFIG_KEY, useConfig } from '../../../lib/config.ts'
import { PageHeader } from '../pages/PageHeader.tsx'
import {
  baselineFrom,
  dirtySections,
  type ListSection,
  move,
  nameFromEmail,
  newItem,
  type PersonRow,
  peopleBatch,
  type SettingsDraft,
} from './draft.ts'
import { CategoriesSection, PrioritiesSection, StatusesSection } from './ListSections.tsx'
import { AddPersonForm, PeopleSection } from './PeopleSection.tsx'

const PEOPLE_KEY = ['users', 'all'] as const

const TABS: {
  id: ConfigSection
  icon: IconType
  color: PaletteColor
  noun: string
  description: string
}[] = [
  {
    id: 'categories',
    icon: LuTag,
    color: 'violet',
    noun: 'category',
    description:
      'The types of issue employees can pick when raising a ticket. Archive a category to hide it from new tickets while keeping it on existing ones.',
  },
  {
    id: 'priorities',
    icon: LuSignal,
    color: 'orange',
    noun: 'priority',
    description: 'Urgency levels, lowest first. The response time sets each ticket’s default ETA.',
  },
  {
    id: 'statuses',
    icon: LuWorkflow,
    color: 'teal',
    noun: 'status',
    description:
      'The workflow stages a ticket moves through, in board order. The stage decides how a status counts: Done stops the ETA clock, Waiting means the ball is with the employee.',
  },
  {
    id: 'people',
    icon: LuUsers,
    color: 'blue',
    noun: 'person',
    description:
      'Everyone who can use HR desk, and what they can do. HR leads can see every confidential ticket.',
  },
]

const CONFLICT = 'Someone else changed settings. Reload to see their changes.'
/** Changes when anyone's row changes (the batch conflict check is per person). */
const usersSig = (users: PublicUser[]) => users.map((u) => `${u.id}@${u.updatedAt}`).join(',')
const LEAVE_PROMPT = 'You have unsaved changes in Settings. Leave without saving?'

function isTab(v: string | null): v is ConfigSection {
  return TABS.some((t) => t.id === v)
}

export function SettingsPage() {
  const config = useConfig()
  const people = useQuery({
    queryKey: PEOPLE_KEY,
    queryFn: () =>
      apiGet('/users?includeArchived=1', (d) => usersListResponseSchema.parse(d).users),
    staleTime: 60_000,
  })
  const authConfig = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => apiGet('/auth/config', (d) => authConfigSchema.parse(d)),
    staleTime: Number.POSITIVE_INFINITY,
  })

  const header = (
    <PageHeader
      title="Settings"
      description="Shape HR desk around how your HR team works. Changes apply for everyone."
    />
  )
  if (config.isError || people.isError)
    return (
      <>
        {header}
        <EmptyState
          icon={LuTriangleAlert}
          color="orange"
          title="Couldn’t load settings"
          action={
            <Button
              onClick={() => {
                void config.refetch()
                void people.refetch()
              }}
            >
              Try again
            </Button>
          }
        >
          {(config.error ?? people.error)?.message ?? 'The server didn’t answer.'}
        </EmptyState>
      </>
    )
  if (!config.data || !people.data)
    return (
      <>
        {header}
        <div
          aria-busy="true"
          className="h-80 animate-pulse rounded-panel border border-border bg-surface"
        >
          <span className="sr-only">Loading settings…</span>
        </div>
      </>
    )
  return (
    <>
      {header}
      <SettingsEditor
        config={config.data}
        users={people.data}
        domain={authConfig.data?.allowedDomain ?? null}
      />
    </>
  )
}

function SettingsEditor({
  config,
  users,
  domain,
}: {
  config: Config
  users: PublicUser[]
  domain: string | null
}) {
  const me = useCurrentUser()
  const qc = useQueryClient()
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const tabParam = params.get('tab')
  const tab: ConfigSection = isTab(tabParam) ? tabParam : 'categories'
  const setTab = useCallback(
    (next: ConfigSection) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p)
          if (next === 'categories') n.delete('tab')
          else n.set('tab', next)
          return n
        },
        { replace: true },
      ),
    [setParams],
  )

  // The editor works on a pinned copy of the server data: live refreshes (someone else saving)
  // never clobber a draft. While nothing is unsaved it follows the server; otherwise a banner
  // offers to reload.
  const [pinned, setPinned] = useState({ config, users })
  const baseline = useMemo(() => baselineFrom(pinned.config, pinned.users), [pinned])
  const [edits, setEdits] = useState<SettingsDraft | null>(null)
  const draft = edits ?? baseline
  const dirty = useMemo(() => dirtySections(draft, baseline), [draft, baseline])
  const isDirty = dirty.size > 0
  const [saving, setSaving] = useState(false)
  const stale =
    pinned.config.version !== config.version || usersSig(pinned.users) !== usersSig(users)
  useEffect(() => {
    if (stale && !isDirty && !saving) {
      setPinned({ config, users })
      setEdits(null)
    }
  }, [stale, isDirty, saving, config, users])
  const [adding, setAdding] = useState(false)
  const [focusId, setFocusId] = useState<string | null>(null)
  const canManageRoles = isHrLead(me)

  // Leaving with unsaved changes: browser close/reload, and in-app navigation.
  useEffect(() => {
    if (!isDirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isDirty])
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  )
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    if (window.confirm(LEAVE_PROMPT)) blocker.proceed()
    else blocker.reset()
  }, [blocker])

  // Focus (and select) the name of a row that was just added.
  useEffect(() => {
    if (!focusId) return
    const el = document.querySelector<HTMLInputElement>(`[data-focus-id="${CSS.escape(focusId)}"]`)
    if (el) {
      el.focus()
      el.select()
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
    setFocusId(null)
  }, [focusId])

  const update = useCallback(
    (fn: (d: SettingsDraft) => SettingsDraft) => setEdits((e) => fn(e ?? baseline)),
    [baseline],
  )

  function listProps<K extends ListSection>(key: K) {
    type T = SettingsDraft[K][number]
    const items = draft[key] as T[]
    return {
      items,
      savedIds: new Set(baseline[key].map((i) => i.id)),
      onChange: (index: number, patch: Partial<T>) =>
        update((d) => ({
          ...d,
          [key]: (d[key] as T[]).map((it, i) => (i === index ? { ...it, ...patch } : it)),
        })),
      onMove: (index: number, dir: -1 | 1) =>
        update((d) => ({ ...d, [key]: move(d[key] as T[], index, dir) })),
      onRemove: (index: number) =>
        update((d) => ({ ...d, [key]: (d[key] as T[]).filter((_, i) => i !== index) })),
    }
  }

  function addItem() {
    if (tab === 'people') {
      setAdding(true)
      return
    }
    const item = newItem(tab)
    update((d) => ({ ...d, [tab]: [...d[tab], item] }))
    setFocusId(item.id)
  }

  function addPerson(email: string) {
    const row: PersonRow = {
      id: `new:${crypto.randomUUID()}`,
      email,
      name: nameFromEmail(email),
      dept: '',
      role: 'member',
      isHrLead: false,
      archived: false,
      avatarUrl: null,
      isNew: true,
    }
    update((d) => ({ ...d, people: [...d.people, row] }))
    setAdding(false)
    setFocusId(row.id)
  }

  const reload = useCallback(() => {
    setEdits(null)
    setAdding(false)
    setPinned({ config, users })
    void qc.invalidateQueries({ queryKey: CONFIG_KEY })
    void qc.invalidateQueries({ queryKey: PEOPLE_KEY })
  }, [qc, config, users])

  async function save() {
    const checked = validateConfig({
      categories: draft.categories,
      priorities: draft.priorities,
      statuses: draft.statuses,
      people: draft.people,
    })
    if (!checked.ok) {
      const first = checked.errors[0]
      if (first) {
        toast(first.message)
        setTab(first.section)
      }
      return
    }
    const value = checked.value
    const normalised: SettingsDraft = {
      categories: value.categories,
      priorities: value.priorities,
      statuses: value.statuses,
      people: value.people ?? draft.people,
    }
    setEdits(normalised)
    setSaving(true)
    let section: ConfigSection = 'categories'
    try {
      if (dirty.has('categories') || dirty.has('priorities') || dirty.has('statuses')) {
        section =
          (['categories', 'priorities', 'statuses'] as const).find((s) => dirty.has(s)) ??
          'categories'
        const saved = await apiSend(
          'PUT',
          '/config',
          {
            version: pinned.config.version,
            categories: normalised.categories,
            priorities: normalised.priorities,
            statuses: normalised.statuses,
          },
          (d) => configSchema.parse(d),
        )
        qc.setQueryData(CONFIG_KEY, saved)
        setPinned((p) => ({ ...p, config: saved }))
        // Keep any People edits; the lists now match what the server stored.
        setEdits((e) => e && { ...e, ...baselineFrom(saved, []), people: e.people })
      }
      if (dirty.has('people')) {
        section = 'people'
        const saved = await apiSend(
          'POST',
          '/users/batch',
          peopleBatch(normalised.people, baseline.people),
          (d) => usersListResponseSchema.parse(d).users,
        )
        qc.setQueryData(PEOPLE_KEY, saved)
        setPinned((p) => ({ ...p, users: saved }))
        void qc.invalidateQueries({ queryKey: ME_KEY })
      }
      setEdits(null)
      setAdding(false)
      toast('Settings saved')
    } catch (err) {
      if (err instanceof ApiError && err.code === 'config_conflict') {
        toast(CONFLICT, { action: { label: 'Reload', onClick: reload } })
      } else if (err instanceof ApiError && err.code === 'users_conflict') {
        toast(USERS_CONFLICT_MESSAGE, { action: { label: 'Reload', onClick: reload } })
        setTab('people')
      } else {
        toast(err instanceof Error ? err.message : 'Couldn’t save settings')
        setTab(section)
      }
    } finally {
      setSaving(false)
    }
  }

  const active = TABS.find((t) => t.id === tab) ?? (TABS[0] as (typeof TABS)[number])
  const counts: Record<ConfigSection, number> = {
    categories: draft.categories.filter((i) => !i.archived).length,
    priorities: draft.priorities.filter((i) => !i.archived).length,
    statuses: draft.statuses.filter((i) => !i.archived).length,
    people: draft.people.filter((i) => !i.archived).length,
  }
  const savedRoles = useMemo(() => new Map(baseline.people.map((p) => [p.id, p.role])), [baseline])

  return (
    <>
      {stale && isDirty && !saving && (
        <div
          role="status"
          className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card border border-note-border bg-note-bg px-3 py-2.5 text-[13.5px]"
        >
          <LuInfo aria-hidden className="size-4 shrink-0 text-amber-fg" />
          <span className="min-w-0 flex-1">
            <span className="font-semibold">Settings were changed by someone else.</span>{' '}
            <span className="text-muted">
              Reload to see their changes (this discards your unsaved edits), or keep editing.
            </span>
          </span>
          <Button size="sm" onClick={reload}>
            Reload
          </Button>
        </div>
      )}
      <div className="grid gap-4 min-[981px]:grid-cols-[200px_minmax(0,1fr)] min-[981px]:gap-7">
        <nav
          data-tour="settings-tabs"
          aria-label="Settings sections"
          className="min-w-0 self-start min-[981px]:sticky min-[981px]:top-[76px]"
        >
          <ul className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1 min-[981px]:mx-0 min-[981px]:flex-col min-[981px]:overflow-visible min-[981px]:px-0 min-[981px]:pb-0">
            {TABS.map((t) => {
              const current = t.id === tab
              const Icon = t.icon
              return (
                <li key={t.id} className="shrink-0">
                  <button
                    type="button"
                    aria-current={current ? 'page' : undefined}
                    data-tour={`settings-tab-${t.id}`}
                    onClick={() => {
                      setTab(t.id)
                      setAdding(false)
                    }}
                    className={cn(
                      'flex h-9 w-full items-center gap-2.5 rounded-ctl px-2.5 text-left font-medium whitespace-nowrap transition-colors',
                      current
                        ? 'bg-accent-soft font-semibold text-text'
                        : 'text-text hover:bg-surface-2',
                    )}
                  >
                    <span
                      data-color={t.color}
                      aria-hidden
                      className="flex size-6 items-center justify-center rounded-input bg-cb text-[15px] text-cf"
                    >
                      <Icon />
                    </span>
                    <span className="flex-1">{SECTION_LABELS[t.id]}</span>
                    {dirty.has(t.id) && (
                      <>
                        <span aria-hidden className="size-1.5 rounded-full bg-amber-fg" />
                        <span className="sr-only">(unsaved)</span>
                      </>
                    )}
                    <span
                      className={cn('tabular text-[12.5px]', current ? 'text-text' : 'text-muted')}
                    >
                      {counts[t.id]}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </nav>

        <section
          aria-labelledby="settings-panel-title"
          className="min-w-0 overflow-hidden rounded-panel border border-border bg-surface"
        >
          <header className="flex items-start gap-3 px-5 pt-5 pb-4 max-[720px]:px-4">
            <span
              data-color={active.color}
              aria-hidden
              className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-ctl bg-cb text-lg text-cf"
            >
              <active.icon />
            </span>
            <div className="min-w-0">
              <h2 id="settings-panel-title" className="text-[17px]">
                {SECTION_LABELS[active.id]}
              </h2>
              <p className="mt-0.5 max-w-[68ch] text-muted">{active.description}</p>
            </div>
          </header>

          {tab === 'categories' && <CategoriesSection {...listProps('categories')} />}
          {tab === 'priorities' && <PrioritiesSection {...listProps('priorities')} />}
          {tab === 'statuses' && <StatusesSection {...listProps('statuses')} />}
          {tab === 'people' && (
            <>
              <PeopleSection
                people={draft.people}
                me={me}
                canManageRoles={canManageRoles}
                savedRoles={savedRoles}
                onChange={(id, patch) =>
                  update((d) => ({
                    ...d,
                    people: d.people.map((p) => (p.id === id ? { ...p, ...patch } : p)),
                  }))
                }
                onRemove={(id) =>
                  update((d) => ({ ...d, people: d.people.filter((p) => p.id !== id) }))
                }
              />
              {adding && (
                <AddPersonForm
                  domain={domain}
                  existingEmails={new Set(draft.people.map((p) => p.email))}
                  onAdd={addPerson}
                  onCancel={() => setAdding(false)}
                />
              )}
            </>
          )}

          <footer className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3.5 max-[720px]:px-4">
            <Button size="sm" onClick={addItem} disabled={tab === 'people' && adding}>
              <LuPlus aria-hidden />
              Add {active.noun}
            </Button>
            <div
              data-tour="settings-save"
              className="ml-auto flex flex-wrap items-center justify-end gap-2"
            >
              <span
                aria-live="polite"
                className={cn('text-[13px]', isDirty ? 'font-medium text-amber-fg' : 'text-muted')}
              >
                {isDirty ? 'Unsaved changes' : 'All changes saved'}
              </span>
              <Button
                variant="ghost"
                disabled={!isDirty || saving}
                onClick={() => {
                  setEdits(null)
                  setAdding(false)
                }}
              >
                Discard
              </Button>
              <Button variant="primary" disabled={!isDirty || saving} onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </footer>
        </section>
      </div>
    </>
  )
}
